import { useParams, Link, useNavigate } from "react-router-dom";
import {
  usePatron,
  usePatronSummary,
  useRenew,
  useCancelReservation,
  useDeletePatron,
} from "@/api/hooks";
import type { Loan, Reservation, PatronStatus } from "@/api/types";
import { useState, useCallback } from "react";
import { PortalAccessCard } from "../components/PortalAccessCard";

const STATUS_LABELS: Record<PatronStatus, string> = {
  active: "Actif",
  blocked: "Bloqué",
  expired: "Expiré",
  suspended: "Suspendu",
};

const STATUS_COLORS: Record<PatronStatus, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  blocked: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  expired: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  suspended: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
};

export function PatronDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const { data: patron, isLoading: patronLoading, error: patronError } = usePatron(id);
  const { data: summary, isLoading: summaryLoading } = usePatronSummary(patron?.barcode);

  const renew = useRenew();
  const cancelReservation = useCancelReservation();
  const deletePatron = useDeletePatron();

  const handleRenew = useCallback(
    async (loan: Loan) => {
      try {
        const result = await renew.mutateAsync({ loanId: loan.id });
        setMessage({
          type: "success",
          text: `Prolongation effectuée - Nouveau retour le ${new Date(result.newDueDate).toLocaleDateString("fr-FR")}`,
        });
      } catch (err) {
        setMessage({
          type: "error",
          text: err instanceof Error ? err.message : "Erreur lors de la prolongation",
        });
      }
    },
    [renew]
  );

  const handleCancelReservation = useCallback(
    async (reservation: Reservation) => {
      if (!confirm("Êtes-vous sûr de vouloir annuler cette réservation ?")) return;

      try {
        await cancelReservation.mutateAsync({ id: reservation.id });
        setMessage({ type: "success", text: "Réservation annulée" });
      } catch (err) {
        setMessage({
          type: "error",
          text: err instanceof Error ? err.message : "Erreur lors de l'annulation",
        });
      }
    },
    [cancelReservation]
  );

  const handleDelete = useCallback(async () => {
    if (!patron) return;
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le lecteur "${patron.displayName}" ?`)) return;

    try {
      await deletePatron.mutateAsync(patron.id);
      void navigate("/circulation/patrons");
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Erreur lors de la suppression",
      });
    }
  }, [patron, deletePatron, navigate]);

  if (patronLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (patronError || !patron) {
    return (
      <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
        Lecteur non trouvé.
        <Link to="/circulation/patrons" className="ml-2 underline">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/circulation/patrons"
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {patron.displayName}
              </h1>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[patron.status]}`}
              >
                {STATUS_LABELS[patron.status]}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {patron.barcode} - {patron.category.name}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/circulation/patrons/${patron.id}/edit`}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Modifier
          </Link>
          <button
            onClick={handleDelete}
            disabled={deletePatron.isPending}
            className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            Supprimer
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`rounded-lg p-4 text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Patron info */}
        <div className="space-y-6 lg:col-span-1">
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Informations</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Email</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{patron.email || "-"}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Téléphone</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {patron.phone || patron.mobilePhone || "-"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Adresse</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {patron.address ? (
                    <>
                      {patron.address}
                      {patron.address2 && <br />}
                      {patron.address2}
                      <br />
                      {patron.postalCode} {patron.city}
                    </>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Bibliothèque</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {patron.homeLocationName}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Inscription</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {new Date(patron.enrollmentDate).toLocaleDateString("fr-FR")}
                </dd>
              </div>
              {patron.expirationDate && (
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Expiration</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {new Date(patron.expirationDate).toLocaleDateString("fr-FR")}
                  </dd>
                </div>
              )}
              {patron.fineBalanceInCents > 0 && (
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Amendes</dt>
                  <dd className="font-medium text-orange-600 dark:text-orange-400">
                    {(patron.fineBalanceInCents / 100).toFixed(2)} €
                  </dd>
                </div>
              )}
              {patron.notes && (
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Notes</dt>
                  <dd className="font-medium whitespace-pre-line text-gray-900 dark:text-white">
                    {patron.notes}
                  </dd>
                </div>
              )}
            </dl>
          </div>
          <PortalAccessCard patron={patron} />
        </div>

        {/* Loans and reservations */}
        <div className="space-y-6 lg:col-span-2">
          {summaryLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : summary ? (
            <>
              {/* Loans */}
              <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    Prêts en cours ({summary.loans.length})
                  </h2>
                  {summary.overdueCount > 0 && (
                    <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-400">
                      {summary.overdueCount} en retard
                    </span>
                  )}
                </div>
                {summary.loans.length > 0 ? (
                  <div className="space-y-3">
                    {summary.loans.map((loan) => (
                      <LoanRow key={loan.id} loan={loan} onRenew={handleRenew} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucun prêt en cours</p>
                )}
              </div>

              {/* Reservations */}
              <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                  Réservations ({summary.reservations.length})
                </h2>
                {summary.reservations.length > 0 ? (
                  <div className="space-y-3">
                    {summary.reservations.map((res) => (
                      <ReservationRow
                        key={res.id}
                        reservation={res}
                        onCancel={handleCancelReservation}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucune réservation</p>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LoanRow({ loan, onRenew }: { loan: Loan; onRenew: (loan: Loan) => void }) {
  const dueDate = new Date(loan.dueDate);

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700/50">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 dark:text-white">{loan.itemTitle}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {loan.itemBarcode} - {loan.checkoutLocationName}
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p
            className={`text-sm font-medium ${loan.isOverdue ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"}`}
          >
            {loan.isOverdue
              ? `${String(loan.daysOverdue)} jour${loan.daysOverdue > 1 ? "s" : ""} de retard`
              : `Retour ${dueDate.toLocaleDateString("fr-FR")}`}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {loan.renewalCount} / {loan.maxRenewals} prolongations
          </p>
        </div>
        {loan.canRenew && (
          <button
            onClick={() => {
              onRenew(loan);
            }}
            className="rounded-lg border border-blue-300 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20"
          >
            Prolonger
          </button>
        )}
      </div>
    </div>
  );
}

function ReservationRow({
  reservation,
  onCancel,
}: {
  reservation: Reservation;
  onCancel: (reservation: Reservation) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700/50">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 dark:text-white">{reservation.biblioRecordTitle}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Retrait: {reservation.pickupLocationName}
          {reservation.estimatedWait && ` - ${reservation.estimatedWait}`}
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${
              reservation.status === "ready"
                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
            }`}
          >
            {reservation.status === "ready"
              ? "Disponible"
              : reservation.status === "to_prepare"
                ? "À préparer"
                : `Position ${String(reservation.queuePosition)}/${String(reservation.totalInQueue)}`}
          </span>
        </div>
        <button
          onClick={() => {
            onCancel(reservation);
          }}
          className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

export default PatronDetailPage;
